// UC20-11 — what the medicine editor writes, and above all what it does NOT write.
// per docs/shape-uc-20-medicine-nomenclator.md §5
//
// The editor shows one code and the position it hangs off. Saving it used to send both
// writes whole: the position with its brand, producer, country and authorisation date
// blank and `active: true`, then the code with the values the form displayed — which are
// the code's own, else the POSITION's. The first erased the position's statement; the
// second copied it onto the code as an administrator-owned value (BUG-1-082). The rule
// here is that a write goes out only for what the administrator changed, and that the
// position write carries the position's identity and nothing else.
import type { MedicineCodeKind } from './medicines-api';

/** What the editor holds. The first six are the POSITION's, the rest this code's own. */
export interface MedicineFormState {
  dci: string;
  pharmaceuticalForm: string;
  dose: string;
  volume: string;
  division: string;
  atcCode: string;
  code: string;
  kind: MedicineCodeKind;
  barcode: string;
  commercialName: string;
  manufacturer: string;
  manufacturerCountry: string;
  authorizationHolder: string;
  authorizationHolderCountry: string;
  authorizationExpiresOn: string;
  active: boolean;
}

const IDENTITY_FIELDS = [
  'dci',
  'pharmaceuticalForm',
  'dose',
  'volume',
  'division',
  'atcCode',
] as const satisfies ReadonlyArray<keyof MedicineFormState>;

/** The five fields that make a medicine unique, plus its ATC: the position's own columns. */
export interface PositionIdentityWrite {
  dci: string;
  pharmaceuticalForm: string;
  dose: string;
  volume: string;
  division: string;
  atcCode: string;
}

/**
 * The position write of an edit, or `null` when nothing of the position's changed.
 *
 * Identity only. The brand, producer, country and authorisation date are this code's
 * (`codePayload`), and `active` is the position's own switch, moved from the register —
 * saving a code must not reactivate an inactivated position.
 */
export function positionIdentityPatch(
  form: MedicineFormState,
  initial: MedicineFormState,
): PositionIdentityWrite | null {
  if (IDENTITY_FIELDS.every((field) => form[field] === initial[field])) return null;
  return {
    dci: form.dci,
    pharmaceuticalForm: form.pharmaceuticalForm,
    dose: form.dose,
    volume: form.volume,
    division: form.division,
    atcCode: form.atcCode,
  };
}

/** A whole-code write: every descriptive field of THIS code goes, an empty one clears. */
export interface CodeWrite {
  code: string;
  kind: MedicineCodeKind;
  authorizationExpiresOn: string;
  commercialName: string;
  manufacturer: string;
  manufacturerCountry: string;
  authorizationHolder: string;
  authorizationHolderCountry: string;
  barcode: string;
}

export function codePayload(form: MedicineFormState): CodeWrite {
  return {
    code: form.code.trim(),
    kind: form.kind,
    // An expiry belongs to an authorisation. Sending one on an unauthorised code would
    // store a date nothing can act on, and UC20-10's clock would then read it.
    authorizationExpiresOn: form.kind === 'AUTHORIZED' ? form.authorizationExpiresOn : '',
    commercialName: form.commercialName.trim(),
    manufacturer: form.manufacturer.trim(),
    manufacturerCountry: form.manufacturerCountry.trim(),
    authorizationHolder: form.authorizationHolder.trim(),
    authorizationHolderCountry: form.authorizationHolderCountry.trim(),
    barcode: form.barcode.trim(),
  };
}

/**
 * The code write of an edit, or `null` when none of the code's own fields changed.
 *
 * A save that only flipped the `Statut` switch must not be a code correction: the write
 * stamps the code as hand-edited (`source`), which the nomenclator sync reads as a
 * reason to leave it alone.
 */
export function codePatch(form: MedicineFormState, initial: MedicineFormState): CodeWrite | null {
  const next = codePayload(form);
  return JSON.stringify(next) === JSON.stringify(codePayload(initial)) ? null : next;
}

/** Creation is one call: the position and its first code together (UC20-08). */
export function createPayload(form: MedicineFormState) {
  return {
    dci: form.dci,
    pharmaceuticalForm: form.pharmaceuticalForm,
    dose: form.dose,
    volume: form.volume,
    division: form.division,
    atcCode: form.atcCode,
    commercialName: form.commercialName.trim(),
    manufacturer: form.manufacturer.trim(),
    manufacturerCountry: form.manufacturerCountry.trim(),
    authorizationExpiresOn: form.kind === 'AUTHORIZED' ? form.authorizationExpiresOn : '',
    active: true,
    code: form.code.trim(),
    codeKind: form.kind,
    codeBarcode: form.barcode.trim(),
  };
}

/**
 * The fields a save refuses to leave empty.
 *
 * A new medicine needs the five identity fields where they apply, the brand and the
 * producer (the wireframe, Q-1-13). Correcting a position that already exists is
 * another thing: the sync serves some positions without a brand or a producer, and a
 * form that refused to save until they were typed would make the one correction the
 * administrator came for — say the pharmaceutical form of such a position — impossible
 * (BUG-1-083). So on an edit those three are required only where the loaded record had
 * a value, which stops the form blanking one without forbidding it to be filled in.
 */
export const ALWAYS_REQUIRED = ['dci', 'pharmaceuticalForm', 'dose'] as const;
export const REQUIRED_WHEN_NEW_OR_FILLED = ['division', 'commercialName', 'manufacturer'] as const;

export type RequiredField =
  | (typeof ALWAYS_REQUIRED)[number]
  | (typeof REQUIRED_WHEN_NEW_OR_FILLED)[number];

export function requiredFields(isEdit: boolean, initial: MedicineFormState): RequiredField[] {
  return [
    ...ALWAYS_REQUIRED,
    ...REQUIRED_WHEN_NEW_OR_FILLED.filter((field) => !isEdit || initial[field].trim().length > 0),
  ];
}

export function missingFields(
  form: MedicineFormState,
  isEdit: boolean,
  initial: MedicineFormState,
): RequiredField[] {
  return requiredFields(isEdit, initial).filter((field) => form[field].trim().length === 0);
}
